import React, { useState, useEffect, useMemo, useRef } from 'react';
import cloneDeep from 'lodash/cloneDeep';

import {
    Button,
    Steps,
    Form,
    App,
} from 'antd';
import {
    UploadOutlined as UploadFileIcon
} from '@ant-design/icons';
import { statusTokens } from '../../theme/theme';
import ImagePreviewModal from './ImagePreviewModal';
import CatalogPreview from './CatalogPreview';
import SavePublishedAsDraftModal from './Modals/SavePublishedAsDraftModal';

// Import step components
import CustomizationStep from './Steps/CustomizationStep';
import BannerStep from './Steps/BannerStep';
import SEOStep from './Steps/SEOStep';
import PermissionsStep from './Steps/PermissionsStep';
import ReviewStep from './Steps/ReviewStep';

// Import services
import {
    isValidDomainFormat,
    isDomainClean,
    isDomainAvailable
} from '../../services/utils/validationUtils';
import {
    formatFullDomainUrl
} from '../../services/utils/domainUtils';
import { convertColorToHex } from '../../services/utils/colorUtils';
import { handleModifyCatalogue, mapDatabaseToForm } from '../../services/utils/catalogueModificationUtils';
import { createDefaultFormStructure } from '../../services/utils/catalogueDefaults';

const { Step } = Steps;

/**
 * A stepper component for modifying a published catalogue
 * This creates a draft version of the published catalogue with a reference back to the original
 * 
 * @param {Object} props - Component props
 * @param {Function} props.onCancel - Function to handle cancel
 * @param {Function} props.onFinish - Function to handle finish
 * @param {number} props.currentStep - Current step index
 * @param {Function} props.setCurrentStep - Function to set current step
 * @param {Function} props.onFormDataChange - Function to handle form data changes
 * @param {Object} props.initialFormData - Initial form data
 * @param {boolean} props.isEditMode - Whether the stepper is in edit mode
 * @param {string} props.stepperKey - Key for the stepper
 * @param {string} props.originalCatalogueId - ID of the original published catalogue
 * @param {boolean} props.isUpdatingExistingDraft - Whether we're updating an existing draft
 * @param {string} props.existingDraftId - ID of the existing draft when updating
 */
const ModifyCatalogStepper = ({
    isDomainInputDisabled, // Added for disabling domain input in specific contexts
    onCancel,
    onFinish,
    currentStep,
    setCurrentStep,
    onFormDataChange,
    initialFormData = null,
    isEditMode = false,
    stepperKey,
    originalCatalogueId,
    onFormInstanceReady, // Callback to expose the form instance to parent components
    isUpdatingExistingDraft = false, // New prop to indicate if we're updating an existing draft
    existingDraftId = null // New prop to pass the existing draft ID when updating
}) => {
    // State for the save as draft confirmation modal
    const [saveAsDraftModalVisible, setSaveAsDraftModalVisible] = useState(false);

    // Handle creation of the modified catalogue using the external utility function
    const handleModifyCatalogueLocal = async () => {
        try {
            await handleModifyCatalogue({
                formData,
                form,
                originalCatalogueId,
                existingDraftId,
                messageApi,
                onSuccess: (finalData, draftedCatalogueId) => {
                    if (onFinish) {
                        onFinish(finalData, draftedCatalogueId);
                    }
                }
            });
        } catch (error) {
            // Error handling is already done in the utility function
            // Just prevent any additional processing if needed
        }
    };

    // Handle confirmation from the modal - Usa la stessa logica della funzione principale
    const handleSaveAsDraftConfirm = async () => {
        // Hide the modal
        setSaveAsDraftModalVisible(false);

        try {
            await handleModifyCatalogue({
                formData,
                form,
                originalCatalogueId,
                existingDraftId,
                messageApi,
                onSuccess: (finalData, draftedCatalogueId) => {
                    if (onFinish) {
                        onFinish(finalData, draftedCatalogueId);
                    }
                }
            });
        } catch (error) {
            // Error handling is already done in the utility function
            // Just prevent any additional processing if needed
        }
    };

    // Handle cancellation from the modal
    const handleSaveAsDraftCancel = () => {
        setSaveAsDraftModalVisible(false);
        messageApi.info('Operation canceled');
    };

    // Get message instance from App context
    const { message: messageApi } = App.useApp();

    // Form state
    const [form] = Form.useForm();

    // Expose form instance to parent component via callback
    useEffect(() => {
        if (onFormInstanceReady && form) {
            onFormInstanceReady(form);
        }
    }, [form, onFormInstanceReady]);

    // Default form data structure wrapped in useMemo to avoid re-creation on each render
    const defaultFormData = useMemo(() => createDefaultFormStructure(), []);


    const [formData, setFormData] = useState(initialFormData || defaultFormData);

    // Reset form when initialFormData changes
    useEffect(() => {
        if (initialFormData) {
            // Use centralized mapping function to convert database structure to form structure
            const processedFormData = mapDatabaseToForm(initialFormData);

            form.setFieldsValue(processedFormData);
            setFormData(processedFormData);
        } else if (!isEditMode) {
            form.setFieldsValue(defaultFormData);
            setFormData(defaultFormData);
        }
    }, [initialFormData, form, defaultFormData, isEditMode]);

    // Notify parent of formData changes for draft logic
    useEffect(() => {
        if (typeof onFormDataChange === 'function') {
            onFormDataChange(formData);
        }
    }, [formData, onFormDataChange]);

    // Keyword input state
    const [keywordInputVisible, setKeywordInputVisible] = useState(false);
    const [keywordInputValue, setKeywordInputValue] = useState('');

    // Image preview modal state
    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewImage, setPreviewImage] = useState('');
    const [previewTitle, setPreviewTitle] = useState('');

    // Validation states
    const [domainValidating, setDomainValidating] = useState(false);
    // Domain URL formatting will be implemented in future versions
    // const [formattedDomainUrl, setFormattedDomainUrl] = useState('');

    // Handle form field changes
    const handleFormChange = (changedValues, allValues) => {
        isFormUpdating.current = true; // Prevent the useEffect from updating the form
        setFormData(prevFormData => {
            let newFormData = cloneDeep(prevFormData);

            // Special handling for SEO fields
            if (changedValues.seo) {
                // Initialize seo object if it doesn't exist
                if (!newFormData.seo) {
                    newFormData.seo = {};
                }

                // Deep merge SEO changes - handle metaDescriptions specially
                if (changedValues.seo.metaDescriptions) {
                    // Initialize metaDescriptions if it doesn't exist
                    if (!newFormData.seo.metaDescriptions) {
                        newFormData.seo.metaDescriptions = {};
                    }

                    // Merge only the changed languages, preserving existing ones
                    newFormData.seo.metaDescriptions = {
                        ...newFormData.seo.metaDescriptions,
                        ...changedValues.seo.metaDescriptions
                    };
                }

                // Merge other SEO fields (excluding metaDescriptions which was handled above)
                const { metaDescriptions, ...otherSeoChanges } = changedValues.seo;
                newFormData.seo = {
                    ...newFormData.seo,
                    ...otherSeoChanges
                };

            }

            // Special handling for Permissions fields
            if (changedValues.permissions) {

                // Initialize permissions object if it doesn't exist
                if (!newFormData.permissions) {
                    newFormData.permissions = {};
                }

                // Merge permissions changes
                newFormData.permissions = {
                    ...newFormData.permissions,
                    ...changedValues.permissions
                };

            }

            // Check if we have color changes in customization
            if (changedValues.customization) {
                const { customization } = changedValues;
                // Process color fields
                if (customization.primaryColor) {
                    newFormData.customization.primaryColor = convertColorToHex(customization.primaryColor);
                }
                if (customization.secondaryColor) {
                    newFormData.customization.secondaryColor = convertColorToHex(customization.secondaryColor);
                }
                if (customization.backgroundColor) {
                    newFormData.customization.backgroundColor = convertColorToHex(customization.backgroundColor);
                }
                if (customization.actionButtonColor) {
                    newFormData.customization.actionButtonColor = convertColorToHex(customization.actionButtonColor);
                }
                if (customization.bannerBackgroundColor) {
                    newFormData.customization.bannerBackgroundColor = convertColorToHex(customization.bannerBackgroundColor);
                }

                // Handle border radius values from BorderRadiusControl
                // Card corners
                if (customization.cardCornerTopLeft !== undefined) newFormData.customization.cardCornerTopLeft = customization.cardCornerTopLeft;
                if (customization.cardCornerTopRight !== undefined) newFormData.customization.cardCornerTopRight = customization.cardCornerTopRight;
                if (customization.cardCornerBottomRight !== undefined) newFormData.customization.cardCornerBottomRight = customization.cardCornerBottomRight;
                if (customization.cardCornerBottomLeft !== undefined) newFormData.customization.cardCornerBottomLeft = customization.cardCornerBottomLeft;

                // Button corners
                if (customization.buttonCornerTopLeft !== undefined) newFormData.customization.buttonCornerTopLeft = customization.buttonCornerTopLeft;
                if (customization.buttonCornerTopRight !== undefined) newFormData.customization.buttonCornerTopRight = customization.buttonCornerTopRight;
                if (customization.buttonCornerBottomRight !== undefined) newFormData.customization.buttonCornerBottomRight = customization.buttonCornerBottomRight;
                if (customization.buttonCornerBottomLeft !== undefined) newFormData.customization.buttonCornerBottomLeft = customization.buttonCornerBottomLeft;

                // Banner corners
                if (customization.bannerCornerTopLeft !== undefined) newFormData.customization.bannerCornerTopLeft = customization.bannerCornerTopLeft;
                if (customization.bannerCornerTopRight !== undefined) newFormData.customization.bannerCornerTopRight = customization.bannerCornerTopRight;
                if (customization.bannerCornerBottomRight !== undefined) newFormData.customization.bannerCornerBottomRight = customization.bannerCornerBottomRight;
                if (customization.bannerCornerBottomLeft !== undefined) newFormData.customization.bannerCornerBottomLeft = customization.bannerCornerBottomLeft;
            }

            // Process all other changes (excluding seo, permissions, and customization which are handled above)
            for (const key in changedValues) {
                if (changedValues.hasOwnProperty(key) && key !== 'customization' && key !== 'seo' && key !== 'permissions') {
                    const value = changedValues[key];

                    if (Array.isArray(key)) {
                        let currentLevel = newFormData;
                        key.forEach((k, index) => {
                            if (index === key.length - 1) {
                                currentLevel[k] = value;
                                if (key[0] === 'banner' && (key[1] === 'heightOption' || key[1] === 'heightMobileOption')) {
                                    if (value !== 'custom') {
                                        const heightField = key[1] === 'heightOption' ? 'height' : 'heightMobile';
                                        currentLevel[heightField] = parseInt(value, 10);
                                    }
                                }
                            } else {
                                if (!currentLevel[k]) {
                                    currentLevel[k] = {};
                                }
                                currentLevel = currentLevel[k];
                            }
                        });
                    } else if (typeof value === 'object' && !Array.isArray(value) && newFormData[key] && typeof newFormData[key] === 'object') {
                        // Merge nested object changes (e.g., banner, etc.)
                        newFormData[key] = { ...newFormData[key], ...value };
                    } else {
                        newFormData[key] = value;
                    }
                }
            }

            return newFormData;
        });
    };

    // Sync form values when formData changes - moved outside of setState to avoid render-time updates
    // Use a ref to track if the update comes from form itself to avoid loops
    const isFormUpdating = useRef(false);

    useEffect(() => {
        if (form && formData && !isFormUpdating.current) {
            isFormUpdating.current = true;
            form.setFieldsValue(formData);
            // Reset the flag after a short delay to allow form updates
            setTimeout(() => {
                isFormUpdating.current = false;
            }, 0);
        }
    }, [form, formData]);

    // Handle image preview
    const handlePreview = (file) => {
        let previewImageUrl = '';
        let previewTitle = '';

        // Handle different file formats
        if (typeof file === 'string') {
            // If file is just a URL string
            previewImageUrl = file;
            previewTitle = 'Image Preview';
        } else if (file instanceof File) {
            // If file is a File object
            previewImageUrl = URL.createObjectURL(file);
            previewTitle = file.name;
        } else if (file && file.originFileObj instanceof File) {
            // If file is an Ant Design Upload file object
            previewImageUrl = URL.createObjectURL(file.originFileObj);
            previewTitle = file.name;
        } else if (file && typeof file.url === 'string') {
            // If file is an object with a URL
            previewImageUrl = file.url;
            previewTitle = file.name || 'Image Preview';
        } else if (file && typeof file.preview === 'string') {
            // If file has a preview URL
            previewImageUrl = file.preview;
            previewTitle = file.name || 'Image Preview';
        } else {
            console.error('Unable to preview file:', file);
            messageApi.error('Unable to preview this file');
            return;
        }

        setPreviewImage(previewImageUrl);
        setPreviewTitle(previewTitle);
        setPreviewVisible(true);
    };

    // Handle keyword input
    const handleKeywordInputChange = (e) => {
        setKeywordInputValue(e.target.value);
    };

    const handleKeywordInputConfirm = () => {
        if (keywordInputValue && !formData.seo.keywords.includes(keywordInputValue)) {
            const newKeywords = [...formData.seo.keywords, keywordInputValue];
            const newSeo = {
                ...formData.seo,
                keywords: newKeywords
            };

            setFormData({
                ...formData,
                seo: newSeo
            });
        }
        setKeywordInputVisible(false);
        setKeywordInputValue('');
    };

    const handleRemoveKeyword = (removedTag) => {
        const newKeywords = formData.seo.keywords.filter(tag => tag !== removedTag);
        const newSeo = {
            ...formData.seo,
            keywords: newKeywords
        };

        setFormData({
            ...formData,
            seo: newSeo
        });
    };

    // Validate allowed domain format
    const isValidAllowedDomain = (domain) => {
        // Basic domain validation regex
        const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9](?:\.[a-zA-Z]{2,})+$/;
        return domainRegex.test(domain);
    };

    // Steps content
    const steps = [
        {
            title: 'Customization',
            content: (
                <CustomizationStep
                    form={form}
                    formData={formData}
                    handleFormChange={handleFormChange}
                    isDomainClean={isDomainClean}
                    isValidDomainFormat={isValidDomainFormat}
                    isDomainAvailable={isDomainAvailable}
                    formatDomainUrl={formatFullDomainUrl}
                    setDomainValidating={setDomainValidating}
                    domainValidating={domainValidating}
                    isDomainInputDisabled={isDomainInputDisabled}
                />
            )
        },
        {
            title: 'Banner',
            content: (
                <BannerStep
                    form={form}
                    formData={formData}
                    handlePreview={handlePreview}
                    handleFormChange={handleFormChange}
                    UploadFileIcon={UploadFileIcon}
                />
            )
        },
        {
            title: 'SEO',
            content: (
                <SEOStep
                    form={form}
                    formData={formData}
                    handleFormChange={handleFormChange}
                    handleKeywordInputChange={handleKeywordInputChange}
                    handleKeywordInputConfirm={handleKeywordInputConfirm}
                    handlePreview={handlePreview}
                    handleRemoveKeyword={handleRemoveKeyword}
                    keywordInputVisible={keywordInputVisible}
                    setKeywordInputVisible={setKeywordInputVisible}
                    keywordInputValue={keywordInputValue}
                />
            )
        },
        {
            title: 'Permissions',
            content: (
                <PermissionsStep
                    form={form}
                    formData={formData}
                    setFormData={setFormData}
                    handleFormChange={handleFormChange}
                    isValidAllowedDomain={isValidAllowedDomain}
                    isDomainInputDisabled={isDomainInputDisabled}
                />
            )
        },
        {
            title: 'Review',
            content: (
                <ReviewStep
                    formData={formData}
                />
            )
        }
    ];

    // Function to handle next step
    const handleNext = () => {
        form.validateFields()
            .then(() => {
                setCurrentStep(currentStep + 1);
                setViewMode('edit'); // Set view to 'edit' on successful next step
            })
            .catch(error => {
                console.error('Validation failed:', error);
                messageApi.error('Please fix the validation errors before proceeding');
            });
    };

    // Function to handle previous step
    const handlePrev = () => {
        setCurrentStep(currentStep - 1);
        setViewMode('edit');
    };

    // Determine if the Next button should be disabled
    const isNextDisabled = () => {
        if (currentStep === 0) {
            // Customization step - check for required fields
            // Only require domain if isDomainInputDisabled is false
            const domainRequired = !isDomainInputDisabled && !formData.domain;
            return !formData.catalogueName || domainRequired;
        } else if (currentStep === 1) {
            // Banner step - check for logo
            return !formData.banner.logo;
        }
        return false;
    };

    // State for controlling preview mode
    const [viewMode, setViewMode] = useState('edit'); // 'edit' or 'preview'

    return (
        <div style={{ width: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
            <ImagePreviewModal
                visible={previewVisible}
                onClose={() => setPreviewVisible(false)}
                imageUrl={previewImage}
                title={previewTitle}
            />
            <SavePublishedAsDraftModal
                isVisible={saveAsDraftModalVisible}
                onCancel={handleSaveAsDraftCancel}
                onConfirm={handleSaveAsDraftConfirm}
                statusTokens={statusTokens}
            />
            <div style={{ display: 'flex', gap: '1.5rem', height: '35rem', width: '100%', boxSizing: 'border-box', overflow: 'hidden' }}>
                {/* Left section with steps */}
                <div style={{ width: '15.625rem', flexShrink: 0, borderRight: '1px solid #f0f0f0', paddingRight: '1rem' }}>
                    <Steps current={currentStep} direction="vertical" style={{ height: '100%' }}>
                        {steps.map(item => (
                            <Step key={item.title} title={item.title} />
                        ))}
                    </Steps>
                </div>

                {/* Main content with title and buttons */}
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', width: 'calc(100% - 17rem)', boxSizing: 'border-box', overflow: 'hidden' }}>
                    <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', paddingLeft: '0.5rem' }}>
                        {viewMode === 'edit' ? (
                            <Button type="primary" onClick={() => setViewMode('preview')}>Preview</Button>
                        ) : (
                            <Button type="primary" onClick={() => setViewMode('edit')}>Go Back</Button>
                        )}
                    </div>

                    {/* Content based on mode */}
                    <div style={{ flex: 1, overflowY: 'auto', padding: '0.5rem', width: '100%', boxSizing: 'border-box' }}>
                        {viewMode === 'edit' ? (
                            // Edit Mode - Show current step content
                            <div className="steps-content" style={{ width: '100%', boxSizing: 'border-box' }}>
                                {steps[currentStep].content}
                            </div>
                        ) : (
                            // Preview Mode - Show catalog preview
                            <CatalogPreview formData={formData} />
                        )}
                    </div>
                </div>
            </div>
            <div className="steps-action" style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'space-between' }}>
                {currentStep > 0 && (
                    <Button onClick={handlePrev}>
                        Previous
                    </Button>
                )}

                {currentStep === 0 && (
                    <Button onClick={onCancel}>
                        Cancel
                    </Button>
                )}

                {currentStep < steps.length - 1 && (
                    <Button type="primary" onClick={handleNext} disabled={isNextDisabled()}>
                        Next
                    </Button>
                )}

                {currentStep === steps.length - 1 && (
                    <Button type="primary" onClick={handleModifyCatalogueLocal}>
                        Save changes
                    </Button>
                )}
            </div>
        </div>
    );
};

export default ModifyCatalogStepper;